/**
 * Auth Guard Component
 *
 * Route protection component that redirects unauthenticated users to login.
 * Used to wrap protected routes.
 */

import { Navigate, useLocation } from 'react-router-dom';

import { LoadingScreen } from '@/shared/components';
import { APP_ROUTES } from '@/shared/constants';

import { useIsAuthenticated, useIsAuthInitialized } from '../../application/store';

import type { ReactNode } from 'react';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Protects routes from unauthenticated access.
 * Shows loading screen while checking auth status.
 * Redirects to login if not authenticated.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const isInitialized = useIsAuthInitialized();

  // Show loading while checking auth status
  if (!isInitialized) {
    return <LoadingScreen />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate
        to={APP_ROUTES.AUTH.ROOT}
        state={{ from: location }}
        replace
      />
    );
  }

  return <>{children}</>;
}

/**
 * Redirects authenticated users away from auth pages.
 * Used for login/register pages.
 */
export function GuestGuard({ children }: AuthGuardProps) {
  const isAuthenticated = useIsAuthenticated();
  const isInitialized = useIsAuthInitialized();

  // Show loading while checking auth status
  if (!isInitialized) {
    return <LoadingScreen />;
  }

  // Redirect to dashboard if already authenticated
  if (isAuthenticated) {
    return <Navigate to={APP_ROUTES.DASHBOARD} replace />;
  }

  return <>{children}</>;
}

